import type { ReactNode } from "react";

import { PERMISSIONS } from "../lib/permissions.ts";
import { analytics } from "../lib/telemetry/analytics.ts";

import { useSession } from "./session.tsx";
import {
  AccessDenied,
  SessionPending,
  SessionProblem,
} from "./SessionScreens.tsx";

/**
 * Lo que se dibuja en lugar de la aplicación mientras no se puede usar.
 *
 * La ruta ya ha intentado recuperar la sesión en `beforeLoad`; esto enseña
 * cómo quedó. Entrar exige `embajadores:global.view` (§10.2): tener sesión en
 * el SSO de lala-ms no es tenerla en Embajadores.
 */
export function RequireSession({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const { state, me } = session;

  // Volviendo al SSO porque la sesión caducó: la aplicación sigue a la vista
  // hasta que el navegador se vaya, sin parpadear a una pantalla de carga.
  const redirecting = state.status === "expired" && state.reason === undefined;

  if (state.status === "authenticated" || (redirecting && me !== null)) {
    if (!session.can(PERMISSIONS.access)) {
      return (
        <AccessDenied
          email={me?.email ?? ""}
          onSignOut={() => {
            analytics.auth.logout({ origin: "access_denied" });
            void session.signOut();
          }}
        />
      );
    }
    return children;
  }

  if (
    (state.status === "anonymous" || state.status === "expired") &&
    state.reason !== undefined
  ) {
    return (
      <SessionProblem
        title={
          state.status === "expired"
            ? "Tu sesión caducó"
            : "No se pudo iniciar sesión"
        }
        reason={state.reason}
        onSignIn={session.signIn}
      />
    );
  }

  return <SessionPending>Entrando…</SessionPending>;
}
